import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { LazySync } from "@tento-ui/lazy";
import { Typography } from "@tento-ui/ui/typography";

@component({
  name: "std-weather-tooltip",
})
export class WeatherTooltip extends LonaWebComponent {
  private static _instance = new LazySync(() => WeatherTooltip.make());
  static get instance(): WeatherTooltip {
    return WeatherTooltip._instance.get();
  }

  static makeWith(day: Option<WeatherTooltip.Day>): WeatherTooltip {
    const $w = WeatherTooltip.make();
    $w.bind(day);
    return $w;
  }

  bind(day: Option<WeatherTooltip.Day>): WeatherTooltip {
    const $attributes = this.$("attributes");
    DomUtils.clearChildren($attributes);
    if (!day) {
      $attributes.appendChild(Typography.small("No weather information"));
      return this;
    }

    const degreeF = "\u00B0F";
    this.$("hero").replaceChildren(
      Typography.h1(`${day.avgtempF}${degreeF}`, "h1"),
    );

    $attributes.appendChild(
      TooltipRow.makeWith({
        label: "High",
        value: `${day.maxtempF}${degreeF}`,
      }),
    );
    $attributes.appendChild(
      TooltipRow.makeWith({
        label: "Low",
        value: `${day.mintempF}${degreeF}`,
      }),
    );
    $attributes.appendChild(
      TooltipRow.makeWith({
        label: "Humidity",
        value: `${day.avghumidity}`,
      }),
    );
    $attributes.appendChild(
      TooltipRow.makeWith({
        label: "uv",
        value: `${day.uv}`,
      }),
    );
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        pointer-events: none;
      }

      #root {
        margin-top: 4px;
        width: 178px;
        border-radius: 12px;
        padding: 2px;
        box-shadow: var(--box-shadow);
        background: var(--bg-elevated);
        color: var(--text-sec);
      }

      #hero {
        height: 120px;
        width: 100%;
        background: var(--bg-surface);
        color: var(--text-pri);
        border-radius: 10px;
        margin-bottom: 2px;
      }

      #attributes {
        padding: 10px;
      }

      #attributes * {
        padding-bottom: 7px;
        margin-bottom: 7px;
        border-bottom: 1px solid var(--divider);
      }

      #attributes *:last-child {
        padding-bottom: 0px;
        margin-bottom: 0px;
        border-bottom: unset;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-center id="hero"></std-center>
      <std-col id="attributes"></std-col>
    </std-col>
  `;
}

@component({
  name: "std-weather-tooltip-row",
})
class TooltipRow extends LonaWebComponent {
  static makeWith(props: { label: string; value: string }): TooltipRow {
    return TooltipRow.make().bind(props);
  }

  bind(props: { label: string; value: string }): TooltipRow {
    this.$("label").textContent = props.label;
    this.$("value").textContent = props.value;
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        justify-content: space-between;
      }

      #value {
        font-weight: 600;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`<std-row id="root">
    <p class="small" id="label"></p>
    <p class="small" id="value"></p>
  </std-row>`;
}

export namespace WeatherTooltip {
  export interface Day {
    condition?: {
      code: number;
    };
    avgtempF: number;
    maxtempF: number;
    mintempF: number;
    avghumidity: number;
    uv: number;
  }
}
